import { useEffect, useRef, useState } from 'react';
import { useCanvasStore } from '../store/canvas-store';
import { resolveLayerAt, dropPosition, liveCfElement } from './layerSelect';
import type { MovePosition } from '../store/types';

/** Elements that may legally hold children (so 'inside' is offered). */
function isContainerTag(el: Element): boolean {
  return !['img', 'input', 'br', 'hr', 'video', 'source', 'svg'].includes(el.tagName.toLowerCase());
}

interface Hint { left: number; top: number; width: number; height: number; pos: MovePosition }

/** Live drop affordance while a layer drag is in progress: a 2px accent line on
 *  the candidate's before/after edge, or an accent ring for 'inside'. Pointer
 *  tracking + drop dispatch live here (the overlay owns the whole gesture once
 *  LayerOverlay's grip sets layerDrag). */
export function LayerDropIndicator() {
  const drag = useCanvasStore((s) => s.layerDrag);
  const setDrag = useCanvasStore((s) => s.setLayerDrag);
  const setLayer = useCanvasStore((s) => s.setLayerSelection);
  const [hint, setHint] = useState<Hint | null>(null);
  const target = useRef<{ refCfNodeId: string; position: MovePosition } | null>(null);

  useEffect(() => {
    if (!drag) { setHint(null); target.current = null; return; }

    const onMove = (e: PointerEvent) => {
      // Single render path: always the unified hit-test (sees through the painted
      // bitmap on Canary; plain elementsFromPoint in the DOM fallback).
      const cand = resolveLayerAt(e.clientX, e.clientY, 'unified');
      const dragNode = liveCfElement(drag.frameId, drag.cfNodeId);
      if (!cand || cand.frameId !== drag.frameId || cand.cfNodeId === drag.cfNodeId) {
        setHint(null); target.current = null; return;
      }
      const candEl = liveCfElement(cand.frameId, cand.cfNodeId);
      if (!candEl || !dragNode || dragNode.contains(candEl)) { setHint(null); target.current = null; return; }
      const r = candEl.getBoundingClientRect();
      const pos = dropPosition(r, e.clientY, isContainerTag(candEl));
      target.current = { refCfNodeId: cand.cfNodeId, position: pos };
      setHint({ left: r.left, top: r.top, width: r.width, height: r.height, pos });
    };

    const onUp = () => {
      if (!useCanvasStore.getState().layerDrag) return; // cancelled (Esc) — don't dispatch
      const t = target.current;
      if (t) {
        const store = useCanvasStore.getState();
        store.beginTransaction('move layer');
        store.dispatch({ t: 'frame.moveNode', frameId: drag.frameId, cfNodeId: drag.cfNodeId, refCfNodeId: t.refCfNodeId, position: t.position });
        store.endTransaction();
        setLayer({ frameId: drag.frameId, cfNodeId: drag.cfNodeId }); // keep selection on the moved node
      }
      setDrag(null);
    };

    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setDrag(null); };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      window.removeEventListener('keydown', onKey);
    };
  }, [drag, setDrag, setLayer]);

  if (!drag || !hint) return null;

  if (hint.pos === 'inside') {
    return (
      <div className="pointer-events-none fixed inset-0 z-40">
        <div className="absolute border-2 border-dashed border-[var(--color-accent)] rounded-[2px]"
          style={{ left: hint.left, top: hint.top, width: hint.width, height: hint.height }} />
      </div>
    );
  }
  const lineTop = hint.pos === 'before' ? hint.top : hint.top + hint.height;
  return (
    <div className="pointer-events-none fixed inset-0 z-40">
      <div className="absolute h-[2px] bg-[var(--color-accent)]"
        style={{ left: hint.left, top: lineTop - 1, width: hint.width }} />
    </div>
  );
}
